import { ArrowRightIcon } from "@phosphor-icons/react";

import { isOutstandingCash } from "@/features/cash/cash.custody";

interface Booking {
  status: string;
  scheduled_at: string;
  payment_method: string;
  /** Null for a hub manager (0285) — the takings block is dropped. */
  amount_due: number | null;
}

interface Remittance {
  status: string | null;
  amount: number;
}

interface MoneyFlowCardProps {
  bookings: Booking[];
  remittances: Remittance[];
}

const peso = (n: number) => `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

/**
 * The cash remittance pipeline (PRD §6.4 pending → received → collected), plus
 * today's takings by payment method for the roles that may see them.
 *
 * ⚠ SINCE 0285 A HUB MANAGER SEES THE PIPELINE ONLY. Their `amount_due` is
 * null — not zero — so the takings block is dropped rather than rendered as ₱0,
 * which would read as "nothing booked today". The pipeline stays: that is cash
 * in their own custody, which they count and receive.
 */
export function MoneyFlowCard({ bookings, remittances }: MoneyFlowCardProps) {
  const today = bookings.filter(
    (b) =>
      b.status !== "cancelled" &&
      new Date(b.scheduled_at).toDateString() === new Date().toDateString(),
  );

  const sumDue = (method: string): number | null =>
    today
      .filter((b) => b.payment_method === method)
      .reduce<number | null>(
        (sum, b) => (sum === null || b.amount_due === null ? null : sum + b.amount_due),
        0,
      );
  const cashDue = sumDue("cash");
  const onlineDue = sumDue("online");
  const giftpassCount = today.filter((b) => b.payment_method.startsWith("giftpass")).length;

  const pipeline = (["pending", "received", "collected"] as const).map((stage) => {
    // ₱0 remittances (0203) are a record that Cash Handling ran, not cash in the
    // pipeline — they never leave `pending` and never reach the later stages, so
    // only the first needs the guard.
    const rows =
      stage === "pending"
        ? remittances.filter(isOutstandingCash)
        : remittances.filter((r) => r.status === stage);
    return { stage, count: rows.length, total: rows.reduce((sum, r) => sum + r.amount, 0) };
  });

  const STAGE_TONES: Record<string, string> = {
    pending: "text-warning",
    received: "text-info",
    collected: "text-success",
  };

  return (
    <div className="space-y-4">
      {cashDue !== null && onlineDue !== null ? (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Today's takings
          </p>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Cash to collect</span>
              <span className="font-semibold tabular-nums">{peso(cashDue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Online (PayMongo)</span>
              <span className="font-semibold tabular-nums">{peso(onlineDue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">GiftPass sessions</span>
              <span className="font-semibold tabular-nums">{giftpassCount}</span>
            </div>
          </div>
        </div>
      ) : null}

      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Remittance pipeline
        </p>
        <div className="flex items-center gap-1">
          {pipeline.map((step, i) => (
            <div key={step.stage} className="flex flex-1 items-center gap-1">
              <div className="flex-1 rounded-lg border border-border bg-background p-2 text-center">
                <p className={`text-xs font-medium capitalize ${STAGE_TONES[step.stage]}`}>
                  {step.stage}
                </p>
                <p className="text-sm font-semibold tabular-nums">{peso(step.total)}</p>
                <p className="type-body-micro text-muted-foreground">
                  {step.count} item{step.count === 1 ? "" : "s"}
                </p>
              </div>
              {i < pipeline.length - 1 ? (
                <ArrowRightIcon
                  weight="fill"
                  size={12}
                  className="shrink-0 text-muted-foreground"
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
